(ns app.front.ui
  (:require ["@carbon/icons-react/es/building/24" :default Building24]
            ["@carbon/icons-react/es/calibrate/24" :default Calibrate24]
            ["@carbon/icons-react/es/information/16" :default Information16]
            ["@carbon/icons-react/es/license/24" :default License24]
            ["@carbon/icons-react/es/map-boundary--vegetation/24" :default MapBoundaryVegetation24]
            ["@carbon/icons-react/es/map-boundary/24" :default MapBoundary24]
            ["@carbon/icons-react/es/security/24" :default Security24]
            ["@carbon/icons-react/es/stamp/24" :default Stamp24]
            ["@carbon/icons-react/es/traffic-cone/24" :default TrafficCone24]
            ["intl-tel-input" :as intl-tel-input-js]
            ["react-day-picker/DayPickerInput" :default DayPickerInput]
            [app.domain.util :as du]
            [app.front.ui.chevron :as c]
            [app.front.ui.common-tags :as ct]
            [app.front.ui.icon-button :as ui-ib]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.text-button :as tb]
            [app.front.ui.transitions :as t]
            [app.front.util :as futil :refer [for-frag]]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]
            [reagent.impl.component]
            [schema-tools.core :as st]
            [schema.core :as s]))


(defn append-to-keyword-ns
  "Appends 'append-part' to namespace part in namespaced keyword 'kw'.

  Example:
  ```
  (append-to-keyword-ns \"page-title\" :process.signal/foo)
   => :process.signal.page-title/foo
   ```"
  [append-part kw]
  (keyword (str (namespace kw) append-part) (name kw)))


(def page-header-el :div.page-header.mb-4)


(defn page-header [_opts & children]
  (into [page-header-el] children))


(defn placeholder-select-item
  ([tr]
   (placeholder-select-item tr :none))
  ([tr value]
   (placeholder-select-item tr value nil))
  ([tr value opts]
   [cc/select-item
    (merge {:key   "placeholder-select-item"
            :text  (tr [:select-option])
            :value value}
           opts)]))

(defn ref-prevent-wheel [ref]
  (when ref
    (.addEventListener ref
                       "wheel"
                       (fn [e]
                         (.preventDefault e))
                       ;; https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#passive
                       ;; passive is true by default on e.g. the wheel event because it can cause lag on scroll.
                       ;; We however need to disable it to allow us to call .preventDefault
                       #js{:passive false})))

(def number-input-props
  {:type    "number"
   :pattern "[0-9]*"
   :ref     ref-prevent-wheel})


; TODO unify with app.front.form.render/number-input
(defn number-input
  "Custom number input avoid problems with Carbon NumberInput, as those expect the value property to be mutable."
  [{:keys [id label-text invalid invalid-text unit-label step light
           disabled required wrapper-class-name hide-unit? hide-label] :as props}]
  [:div.bx--form-item.bx--number.cloudpermit--number-input
   {:class (when light "bx--number--light")}
   [:label.bx--label {:for   id
                      :class [(when disabled "bx--label--disabled")
                              (when required "required-field")
                              (when hide-label "bx--visually-hidden")]}
    label-text]
   [:div.number-input-wrapper
    (p/assoc-if {:class wrapper-class-name}
                :data-invalid (when invalid "true"))
    [:input (merge number-input-props
                   {:id   id
                    :step (or step 1)}
                   (when light
                     {:class "bx--number-input--light"})
                   (select-keys props [:value :default-value :placeholder :read-only
                                       :on-change :on-blur :on-key-up :disabled
                                       :min :max :required]))]
    (when-not hide-unit?
      [:div.input-unit unit-label])]
   (when-not (str/blank? invalid-text)
     [:div.bx--form-requirement
      invalid-text])])


(defn time-input
  "Clock time input
  Presents the user with an AM/PM clock representation,
  but in/outputs a tuple with the hours and minutes in the 24h format.
  All user input components will use AM/PM according to a conversation on Slack:
  https://cloudpermit.slack.com/archives/CEQ7N7G9G/p1592304467000300?thread_ts=1592221577.002600&cid=CEQ7N7G9G"
  [{[hours mins] :value
    :keys        [id label-text on-change light? required? disabled? invalid? invalid-text min-step show-clear-btn clear-btn-text]
    :as          props}]
  (let [[hours am-or-pm] (date-util/hours->hours-am-or-pm hours)
        on-change-fn     (fn [hours minutes am-or-pm]
                           (on-change [(date-util/hours-am-or-pm->hours (mod hours 12) am-or-pm)
                                       (mod minutes 60)]))
        input-class-name ["px-0 w-12"
                          (when light? "bg-white")]
        disabled?        (boolean disabled?)
        label-id         (str "label-" id)]
    [:div.time-input
     (dissoc props :value :label-text :on-change :light? :required? :disabled? :invalid? :invalid-text :min-step :show-clear-btn :clear-btn-text)
     (when (some? label-text)
       [:label.bx--label.block
        {:id    label-id
         :class (when required? "required-field")}
        label-text])
     [:div.relative.flex
      {:class        (when invalid? "invalid-composite-input")
       :data-invalid (when invalid? "true")}
      [:input
       (merge number-input-props
              {:id              (str id "-hours")
               :data-cy         "hours-input"
               :aria-labelledby label-id
               :class-name      input-class-name
               :step            1
               :placeholder     "HH"
               :min             1
               :max             12
               :value           hours
               :disabled        disabled?
               :required        required?
               :on-change       #(on-change-fn (-> % futil/tval futil/parse-int) mins am-or-pm)})]
      [:span.separator.pointer-events-none ":"]
      [:input
       (merge number-input-props
              {:id              (str id "-minutes")
               :data-cy         "minutes-input"
               :aria-labelledby label-id
               :class-name      input-class-name
               :step            (or min-step 15)
               :placeholder     "MM"
               :min             0
               :max             59
               :value           (if (and (some? mins) (< mins 10))
                                  (str 0 mins)
                                  mins)
               :disabled        disabled?
               :on-change       #(on-change-fn hours (-> % futil/tval futil/parse-int) am-or-pm)})]
      (cc/select
        (p/assoc-if {:value           (or am-or-pm "")
                     :label-text      ""
                     :hide-label      true
                     :aria-labelledby label-id
                     :id              (str id "-am-pm")
                     :on-change       #(on-change-fn hours mins (-> % futil/tval keyword))
                     :disabled        disabled?}
                    :class-name (when light? "bx--select-white"))
        (cc/select-item {:value "AM" ; Select item will coerce keyword values to strings anyway
                         :text  "AM"})
        (cc/select-item {:value "PM"
                         :text  "PM"}))
      (when show-clear-btn
        [:div.px-2
         (cc/button
           {:disabled      false
            :type          :button
            :size          :field
            :kind          :tertiary
            :on-click      #(on-change nil)
            :icon          i/remove
            :icon-position :right}
           clear-btn-text)])]
     (when (not-empty invalid-text)
       [:div.bx--form-requirement invalid-text])]))


(defn text-input [{:keys [id label-text helper-text light type
                          hide-label disabled invalid invalid-text
                          class input-class required data-cy label-class] :or {type :text} :as props}]
  [:div.bx--form-item
   (-> {:class class}
       (p/assoc-if :data-cy data-cy))
   [:label.bx--label
    {:for   id
     :class [(when hide-label "bx--visually-hidden")
             (when disabled "bx--label--disabled")
             (when required "required-field")
             label-class]}
    label-text]
   [:input.bx--text-input.bx--text__input.text-base.md:text-sm
    (p/assoc-if (select-keys props [:value :default-value :placeholder :max-length
                                    :id :pattern :auto-focus :key :auto-complete
                                    :on-change :on-blur :on-focus :on-key-up :on-key-down
                                    :required :ref])
                :type type
                :class [input-class
                        (when light "bx--text-input--light bg-white")]
                :data-invalid (when invalid "true")
                :aria-invalid (when invalid "true")
                :aria-describedby (when invalid (str id "-error-msg"))
                :disabled (when disabled (boolean disabled)))]
   (when-not (str/blank? helper-text)
     [:div.bx--form__helper-text
      {:class (when disabled "bx--form__helper-text--disabled")}
      helper-text])
   (when (and invalid (not (str/blank? invalid-text)))
     [:div.bx--form-requirement {:id (str id "-error-msg")}
      invalid-text])])


(s/defschema SearchProps (st/open-schema {:id                      s/Str
                                          :close-button-label-text s/Str}))


(s/defn search [{:keys [id label-text value close-button-label-text disabled
                        hide-label class class-name clear-fn show-clear? icon-position
                        invalid-term? search-in-progress? loading-text
                        container-class light loading-style size input-style]          :as props} :- SearchProps]
  [:div.relative
   {:class [(when invalid-term? "border border-solid border-red-600")
            (when search-in-progress? "search--loading")
            container-class]}
   (when search-in-progress?
     [:div.absolute.z-10 {:style (merge (case size
                                          :small {:top "0px" :left "8px"}
                                          :medium {:top "4px" :left "12px"}
                                          {:top "8px" :left "16px"})
                                        loading-style)}
      (cc/inline-loading {:icon-description loading-text})])
   [:div.bx--search
    {:class (case size
              :small "bx--search--sm"
              :medium "bx--search--lg"
              "bx--search--xl")}
    [:label.bx--label
     {:for   id
      :class [(when hide-label "bx--visually-hidden")
              (when disabled "bx--label--disabled")]}
     label-text]
    (cc/icon {:icon        i/search
              :description ""
              :aria-hidden true
              :class-name  (str "bx--search-magnifier"
                                (when (= icon-position :right)
                                  " !left-auto right-4"))
              :fill        "currentcolor" ; Fill is overridden by bx--search-magnifier
              :view-box    "0 0 16 16"
              :height      "16"
              :width       "16"})
    [:input.bx--search-input
     (p/assoc-if (select-keys props [:value :default-value :placeholder
                                     :type :id :pattern :auto-focus :key :auto-complete
                                     :on-change :on-blur :on-key-up :required])
                 :class [class
                         class-name
                         (when light "bg-white")]
                 :style input-style
                 :type :text
                 :disabled (when disabled (boolean disabled)))]
    (when-not (false? show-clear?)
      [:button.bx--search-close
       {:type       :button
        :class      (when (empty? value) "bx--search-close--hidden")
        :title      close-button-label-text
        :aria-label close-button-label-text
        :on-click   clear-fn}
       (cc/icon {:icon        i/remove
                 :description ""
                 :aria-hidden true
                 :fill        "currentcolor"
                 :view-box    "0 0 16 16"
                 :height      "16"
                 :width       "16"})])]])


(defn text-area [{:keys [id
                         label-text
                         invalid
                         invalid-text
                         light
                         helper-text
                         hide-label
                         disabled
                         required
                         rows
                         cols
                         class
                         label-class
                         input-class
                         data-cy]
                  :as   props}]
  [:div.bx--form-item {:class class}
   (when label-text
     [:label.bx--label {:for   id
                        :class [(when hide-label "bx--visually-hidden")
                                (when disabled "bx--label--disabled")
                                (when required "required-field")
                                label-class]}
      label-text])
   (when-not (str/blank? helper-text)
     [:div.bx--form__helper-text
      {:class (when disabled "bx--form__helper-text--disabled")}
      helper-text])
   [:div.bx--text-area__wrapper
    (p/assoc-if {} :data-invalid (when invalid "true"))
    [:textarea.bx--text-area
     (p/assoc-if (select-keys props [:value :default-value :placeholder
                                     :type :id :pattern :auto-focus :key
                                     :on-change :on-blur :on-key-up :ref
                                     :on-key-down])
                 :rows (or rows 4)
                 :cols (or cols 50)
                 :class [(when light "bx--text-area--light")
                         input-class
                         (when invalid "bx--text-area--invalid")]
                 :data-invalid (when invalid "true")
                 :aria-invalid (when invalid "true")
                 :aria-describedby (when invalid (str id "-error-msg"))
                 :disabled (when disabled (boolean disabled))
                 :data-cy (when data-cy data-cy))]]
   (when (and invalid invalid-text)
     [:div.bx--form-requirement {:id   (str id "-error-msg")
                                 :role "alert"}
      invalid-text])])


(s/defschema ButtonOptions
  (merge
    cc/ButtonOptions
    {(s/optional-key :active?) s/Bool}))


(s/defn button
  "Button
   `props` {:id :on-click :disabled :icon :active?
            :size #{:default :field :small}
            :kind #{:primary :secondary :tertiary :ghost :danger}}
   `children` - contents of the button"
  [props :- ButtonOptions
   & children]
  (apply cc/button
         (-> props
             (update :kind #(-> {:tertiary  :ghost ;; Map naming to match style guide
                                 :secondary :tertiary}
                                (get % (or % :primary)))) ;; Default to :primary
             (dissoc :active?)
             (update :class-name #(str % (when (:active? props)
                                           " !bg-ui-night-sky-100 !border-ui-night-sky-100 !text-ui-white"))))
         children))

(defn back-button
  "Reusable back button with left arrow icon.
   `label` - text to display
   `on-click` - click handler function"
  [{:keys [label on-click]}]
  [:button.w-full.bg-transparent.border-0.p-2.flex.items-center.gap-2.cursor-pointer
   {:on-click on-click
    :type     "button"}
   [cc/icon {:icon i/arrow-left :description label :width "16" :height "16"}]
   [:span label]])

(defn expand-button
  "Expand/collapse toggle button with an integrated chevron"
  [{:keys [button-props expanded? expand-handler title-expanded title-collapsed tr]}]
  (let [title (if expanded? title-expanded title-collapsed)]
    [button
     (merge {:kind          :tertiary
             :on-click      expand-handler
             :aria-expanded expanded?}
            (when-not title {:aria-label (tr [(if expanded? :close :open)])})
            button-props
            {:class-name (str "section-expand-button " (:class-name button-props))})
     (when title [:span.mr-2 title])
     [c/chevron {:tr        tr
                 :side      :right
                 :expanded? expanded?
                 :size      (if title 16 24)}]]))


(s/defschema ModalConfirmationTriggerButtonOpts
  (st/dissoc cc/ButtonOptions :on-click :children))


(s/defschema ModalConfirmationButtonOpts
  (st/merge
    {:tr (s/pred fn?)}
    (st/optional-keys
      {:id                     s/Str
       :wrapper-class-name     s/Str
       :modal-title            s/Str
       :modal-class            s/Str
       :header-class           s/Str
       :body-class             s/Str
       :on-key-down            (s/pred fn?)
       :selector-primary-focus s/Str
       :on-yes-click           (s/pred fn?)
       :skip-confirm?          s/Bool
       :disabled?              s/Bool
       :dangerous?             s/Bool
       :text-button?           s/Bool
       :icon-button?           s/Bool
       :yes-disabled?          s/Bool
       :yes-button-text        s/Str
       :no-button-text         s/Str
       :trigger-button-text    s/Str
       :trigger-button-opts    ModalConfirmationTriggerButtonOpts
       :trigger-icon-opts      (st/dissoc cc/IconOptions :on-click)
       :on-modal-open          (s/pred fn?)})))


(defn confirm-modal [{:keys [id tr open? on-close on-yes-click yes-disabled? class dangerous?]
                      :as   opts} & modal-body]
  (r/with-let [on-close-fn     (futil/make-callback-fn
                                 (fn [on-close _]
                                   (on-close)
                                   true))
               on-yes-click-fn (futil/make-callback-fn
                                 (fn [on-close on-yes-click e]
                                   (when on-yes-click
                                     (on-yes-click e))
                                   (on-close)))]
    [ui-modal/composed-input-modal
     (merge
       {:open                   open?
        :selector-primary-focus "button.bx--modal-close"
        :class-name             (str (or class "") " confirm-modal-" id)
        :on-close               (on-close-fn on-close)}
       (-> opts
           (select-keys [:on-key-down :selector-primary-focus :wrapper-class-name :size])
           (set/rename-keys {:wrapper-class-name :class-name})))
     (cc/modal-header
       {:class-name  (or (:header-class opts) "")
        :close-modal on-close
        :title       (or (:modal-title opts) (tr [:are-you-sure]))})
     (into (cc/modal-body {:class-name (or (:body-class opts) "")}) modal-body)
     (cc/modal-footer
       {}
       (cc/button
         {:kind     :secondary
          :data-cy  (str "confirm-modal-no-" id)
          :on-click on-close}
         (or (:no-button-text opts) (tr [:no])))
       (cc/button
         {:kind     (if dangerous? :danger :primary)
          :data-cy  (str "confirm-modal-yes-" id)
          :disabled (boolean yes-disabled?)
          :on-click (on-yes-click-fn on-close on-yes-click)}
         (or (:yes-button-text opts) (tr [:yes]))))]))


(s/defn modal-confirmation-button
  "Generic button, which has confirmation modal built in.

  Required keys are `:id`, `:tr` and most likely you also
  want to define `:on-yes-click` event handler fn.

  Children become cc/modal-body. Set `:skip-confirm?` to true to call `:on-yes-click` immediately;
  changing it keeps the trigger mounted so an in-progress click is not lost.

  See `ModalConfirmationButtonOpts` for all options."
  [{:keys [id trigger-button-text trigger-button-opts modal-class on-modal-open
           text-button? icon-button? trigger-icon-opts skip-confirm? on-yes-click]
    :as   opts} :- ModalConfirmationButtonOpts
   & modal-body]
  (r/with-let [*modal-open? (r/atom false)
               on-close     #(reset! *modal-open? false)
               on-click-fn  (futil/make-callback-fn
                              (fn [skip-confirm? on-modal-open on-yes-click e]
                                (if skip-confirm?
                                  (when on-yes-click (on-yes-click e))
                                  (do
                                    (when on-modal-open (on-modal-open))
                                    (swap! *modal-open? not)))))]
    (let [on-click  (on-click-fn skip-confirm? on-modal-open on-yes-click)
          disabled  (boolean (:disabled? opts))
          base-opts (util/mmap on-click disabled)]
      [:<>
       (cond
         text-button?
         [tb/text-button (merge base-opts
                                (set/rename-keys trigger-button-opts {:class-name :class}))
          trigger-button-text]

         icon-button?
         [ui-ib/icon-button (merge base-opts
                                   trigger-icon-opts)]

         trigger-icon-opts
         (cc/icon (merge (util/mmap on-click)
                         trigger-icon-opts))

         :else
         (cc/button
           (merge base-opts
                  (when (and (:icon trigger-button-opts)
                             (seq trigger-button-text))
                    {:icon-description ""})
                  trigger-button-opts)
           trigger-button-text))
       (into [confirm-modal
              (merge opts
                     {:open?    @*modal-open?
                      :id       id
                      :on-close on-close
                      :class    modal-class})]
             modal-body)])))

(defn text-rectangle
  "Rectangle shaped component that displays given text in the center of the rectangle.
  Almost like Tile."
  [text]
  [:div.cloudpermit--rectangle.cloudpermit--text-rectangle
   [:span text]])


(defn intl-tel-input
  "Wrapper for https://github.com/jackocnr/intl-tel-input.
  Parameters [optional]:
  [`id`] Id for the underlying (Carbon-styled) input. Default is a unique id.
  [`label-text`] Text for the component label. If not given, no
  label. Blank text denotes empty label.
  [`light`] If true the the input is rendered in the light style (default false).
  [`value`] Initial value for the component.
  [`placeholder`] Placeholder text for the input.
  [`invalid`] Indicates that the value is invalid (default false).
  [`invalid-text`] Error text to be shown when `invalid` is true.
  [`disabled`] If true, the input is disabled (default false).
  [`callback`] Function to be called when the phone number
  changes. The function argument consists of country dial
  code (prefixed with + and can be omitted if not selected) and the
  input value separated by space. If input value is blank, the
  callback parameter is also empty.
  [`on-blur`] Optional function called with the same normalized value as `callback` when the input
  loses focus."
  [{:keys [id label-text light value placeholder
           invalid invalid-text disabled callback on-blur required initial-country]}]
  (r/with-let [iti* (r/atom nil)]
    (let [id               (or id (futil/unique-id "iti"))
          value            (or value "")
          normalized-value (fn [v]
                             (let [v  (str/trim v)
                                   cc (when-let [iti @iti*]
                                        (.-dialCode ^js (.getSelectedCountryData ^js iti)))]
                               (cond
                                 (str/blank? v) ""
                                 (str/blank? cc) v
                                 :else (str "+" cc " " v))))
          callback         (if callback
                             (fn [v]
                               (let [v (normalized-value v)]
                                 (when-not (= v value)
                                   (callback v))))
                             identity)
          blur-callback    (when on-blur
                             (fn [v]
                               (on-blur (normalized-value v))))]
      [:div.bx--form-item.bx--text-input-wrapper
       (when label-text
         [:label.bx--label {:for   id
                            :class (when required "required-field")} label-text])
       [:div.bx--text-input__field-wrapper
        (util/assoc-when {} :data-invalid invalid)
        (when invalid
          [:svg.bx--text-input__invalid-icon
           {:focusable             false
            :preserve-aspect-ratio "xMidYMid meet"
            :xmlns                 "http://www.w3.org/2000/svg"
            :width                 "16"
            :height                "16"
            :viewBox               "0 0 16 16"
            :aria-hidden           true
            :style                 {:will-change :transform
                                    :z-index     1}}
           [:path {:d "M8 1C4.2 1 1 4.2 1 8s3.2 7 7 7 7-3.1 7-7-3.1-7-7-7zm-.5 3h1v5h-1V4zm.5 8.2c-.4 0-.8-.4-.8-.8s.3-.8.8-.8c.4 0 .8.4.8.8s-.4.8-.8.8z"}]
           [:path {:d              "M7.5 4h1v5h-1V4zm.5 8.2c-.4 0-.8-.4-.8-.8s.3-.8.8-.8c.4 0 .8.4.8.8s-.4.8-.8.8z"
                   :data-icon-path "inner-path"
                   :opacity        "0"}]])
        [:div.iti-wrapper {:class (futil/css-flags :iti-wrapper--disabled disabled)}
         [:input {:id          id
                  :type        "tel"
                  :disabled    disabled
                  :placeholder placeholder
                  :class       (concat [:bx--text-input :bx--text__input]
                                       (futil/css-flags :bx--text-input--light light
                                                        :bx--text-input--invalid invalid))
                  :required    required
                  :ref         (fn [elem]
                                 (when (and elem (nil? @iti*))
                                   (let [new-iti (intl-tel-input-js elem
                                                                    (clj->js {:separateDialCode   true
                                                                              :initialCountry     initial-country
                                                                              :preferredCountries ["ca", "us"]}))]
                                     (reset! iti* new-iti)
                                     (.setNumber ^js new-iti value))))
                  :on-change   #(some-> % futil/tval callback)
                  ;; Only attach when on-blur was given; else blur-callback is nil and `(nil v)` throws.
                  :on-blur     (when blur-callback #(some-> % futil/tval blur-callback))}]]]
       (when invalid
         [:div.bx--form-requirement
          {:id (str "input-" id "-error-msg")}
          invalid-text])])
    (finally
      (swap! iti* (fn [iti]
                    (when iti (.destroy ^js iti))
                    nil)))))


(def date-picker-input
  (r/reactify-component
    (fn [props]
      [:<>
       [:input (merge props {:class         ["bx--date-picker__input" (:className props)]
                             :auto-complete :off
                             :style         {:width (:width props)}})]
       (when-not (:data-clearable props)
         (cc/icon {:class-name  "absolute pointer-events-none"
                   :style       {:bottom  "0.8rem"
                                 :right   "0.8rem"
                                 :opacity (if (:disabled props) "0.2" "1")}
                   :icon        i/calendar
                   :width       "14"
                   :height      "14"
                   :description ""
                   :aria-hidden true}))])))


(defn- date-picker-overlay-position
  "Opens an overlay that would cross the right or bottom edge of the window leftwards from the right
  edge of the input, or above the input when there is room."
  [^js overlay]
  (let [rect         (.getBoundingClientRect overlay)
        input-height (some-> overlay (.closest ".DayPickerInput") (.querySelector "input") .-offsetHeight)]
    (p/assoc-if {}
                :right (when (> (.-right rect) (.-innerWidth js/window))
                         0)
                :bottom (when (and input-height
                                   (> (.-bottom rect) (.-innerHeight js/window))
                                   (> (- (.-top rect) input-height) (.-height rect)))
                          input-height))))


(def date-picker-overlay
  (r/reactify-component
    (fn [_]
      (let [*position (r/atom nil)
            ;; Created once, so the overlay is measured only when it mounts
            on-ref    #(when (and % (nil? @*position))
                         (reset! *position (date-picker-overlay-position %)))]
        (fn [props]
          [:div.absolute.z-30.bg-white.shadow-xl.animation-appear-slide-down
           (-> props
               (dissoc :classNames :selectedDay)
               (assoc :ref on-ref
                      :style @*position))])))))


(def date-picker-overlay-fixed
  (r/reactify-component
    (fn [props]
      [:div.fixed.z-50.bg-white.shadow-xl.animation-appear-slide-down
       (dissoc props :classNames :selectedDay)])))


(defn date-picker-caption-element [{:keys [tr ^js date on-change]}]
  (let [selected-month      (.getMonth date)
        selected-year       (.getFullYear date)
        tr?                 #(if (some? tr) (tr %) "")
        selected-month-name (futil/tr-month tr selected-month)
        select-prev-month   #(on-change (js/Date. selected-year (dec selected-month)))
        select-next-month   #(on-change (js/Date. selected-year (inc selected-month)))
        select-prev-year    #(on-change (js/Date. (dec selected-year) selected-month))
        select-next-year    #(on-change (js/Date. (inc selected-year) selected-month))]
    [:div.DayPicker-Caption.px-0
     [:div.flex.flex-row
      [:div.flex.flex-row.flex-auto.p-2.bg-gray-100.text-sm.items-center.mr-1
       (cc/icon {:icon        i/chevron-small-left
                 :class-name  "cursor-pointer"
                 :fill        "var(--ui-violet-100)"
                 :description (tr? [:datepicker/prev-month])
                 :on-click    select-prev-month})
       [:div.flex.flex-auto.text-center.justify-center selected-month-name]
       (cc/icon {:icon        i/chevron-small-right
                 :class-name  "cursor-pointer"
                 :fill        "var(--ui-violet-100)"
                 :description (tr? [:datepicker/next-month])
                 :on-click    select-next-month})]
      [:div.flex.flex-row.p-2.bg-gray-100.text-sm.items-center
       (cc/icon {:icon        i/chevron-small-left
                 :class-name  "cursor-pointer"
                 :fill        "var(--ui-violet-100)"
                 :description (tr? [:datepicker/prev-year])
                 :on-click    select-prev-year})
       [:div.w-12.text-center.justify-center selected-year]
       (cc/icon {:icon        i/chevron-small-right
                 :class-name  "cursor-pointer"
                 :fill        "var(--ui-violet-100)"
                 :description (tr? [:datepicker/next-year])
                 :on-click    select-next-year})]]]))


(def date-picker-navbar-element
  "Hide default month switcher arrows because they are rendered in caption element"
  (r/reactify-component
    (fn [] [:div])))

(defn- non-date?
  [value]
  (and (some? value)
       (not (or (inst? value)
                (= "" value)))))

(defn date-picker
  "Date-picker input

  NOTE that the underlying datatype is a timestamp at noon on the user's time zone!

   options:
    `tr`:                the translation function
    `id`:                id of the component
    `label-loc`:         localization key for label
    `label-text`:        localization text for label
    `hide-label`:        if true, hides the label
    `disabled?`:         if true, disable the datepicker
    `required?`:         if true, show the required asterisk in label
    `value`:             current value of the datepicker (js date object)
    `callback`:          will be called after successful selection with a js date object at 12:00 on browser tz as the only argument
    `render-day`:        specify a function to render a day cell, gets the corresponding date as the only argument
    `min-date`:          minimum selectable date
    `max-date`:          maximum selectable date
    `allow-date?`:       function to check whether the date given as the only argument is allowed to be selected
    `invalid?`:          if true, show red border around input
    `invalid-text`:      error text to show when invalid
    `class-name`:        css classes to be added to the wrapper element
    `fixed?`:            if true, renders the datepicker overlay with fixed position, defaults to absolute position
    `empty-value`:       specify a value representing empty selection, defaults to nil
    `clearable?`:        if true, show a button that clears the selected date
    `class-name`:        string containing additional css classes
    `class-input`:       string containing additional css classes for the input
    `report-disallowed?` if true, also call `callback` when disallowed date gets selected (as text, not possible by clicking)"
  [{:keys [value empty-value]}]
  (let [value            (when-not (non-date? value) value)
        *value           (r/atom (when-not (= value empty-value) value))
        ;; Only show red invalid border on the input after it has been modified, not right away on nil initial value
        *dirty?          (r/atom (some? value))
        *month           (r/atom (or @*value (js/Date.)))
        prepare-renderer (fn [renderer]
                           (fn [day mods]
                             (-> #(renderer day mods)
                                 r/reactify-component
                                 r/create-element)))]
    (r/create-class
      {:display-name
       "app.front.ui/date-picker"
       :constructor
       (fn [this]
         (set! (.-state this) #js {}))

       ; FIXME: This would probably make more sense as a "controlled component" instead of this internal *value atom
       :get-derived-state-from-props
       (fn [{:keys [value]}]
         (reset! *value (when-not (= value empty-value) value)))

       :reagent-render
       (fn [{:keys [tr label-loc label-text disabled? callback render-day min-date max-date allow-date? invalid?
                    invalid-text class-name class-input fixed? empty-value width required? id hide-label
                    report-disallowed? placeholder clearable?]}]
         (let [allowed-date?   (fn [date]
                                 (and
                                   (some? (str-util/blank->nil date))
                                   (or (nil? min-date) (date-util/on-or-after? date (js/Date. min-date)))
                                   (or (nil? max-date) (date-util/before? date (date-util/add-days-to-date (js/Date. max-date) 1)))
                                   (or (nil? allow-date?) (allow-date? date))))
               tr              (or tr (<sub [:tr]))
               modifiers       {:disabled #(not (allowed-date? %))}
               on-day-change   (fn [day, _modifiers, _day-picker-input]
                                 (reset! *value day)
                                 (reset! *dirty? true)
                                 (cond
                                   (nil? day) (callback empty-value)
                                   (allowed-date? day) (callback day)
                                   report-disallowed? (callback day)))
               label           (or label-text (when (some? label-loc) (tr [label-loc])))
               on-month-change #(reset! *month %)
               caption-element (r/reactify-component
                                 (fn [props]
                                   [date-picker-caption-element
                                    (-> props
                                        (assoc :on-change on-month-change)
                                        (assoc :tr tr))]))
               weekdays        (->> (range 7)
                                    (mapv (partial futil/tr-weekday tr)))
               weekdays-short  (->> (range 7)
                                    (mapv #(futil/tr-weekday-short tr (str %))))
               format-date     (<sub [::date-util/date-fn])
               placeholder     (or placeholder (tr [:date-placeholder]))
               clear-button?   (and clearable? @*value (not disabled?))]
           [:div.bx--date-picker.bx--date-picker--single
            {:class (str class-name)}
            [:div.bx--date-picker-container
             {:class (str (when (nil? label) "bx--date-picker--nolabel"))
              :style {:width (or width "auto")}}
             (when (some? label)
               [:label.bx--label (p/assoc-if {:class [(when required? "required-field")
                                                      (when hide-label "bx--visually-hidden")]}
                                             :for (when id (str id "-input")))
                label])
             [:> DayPickerInput
              {:class-names       {:container      "DayPickerInput bx--date-picker-input__wrapper"
                                   :overlayWrapper "DayPickerInput-OverlayWrapper"
                                   :overlay        "DayPickerInput-Overlay"}
               :value             @*value
               :on-day-change     on-day-change
               :overlay-component (if fixed? date-picker-overlay-fixed date-picker-overlay)
               :component         date-picker-input
               :input-props       {:disabled       disabled?
                                   :width          (or width "287px")
                                   ;; false is not acceptable, use when
                                   :data-invalid   (when (or invalid?
                                                             (non-date? @*value)
                                                             (and required?
                                                                  @*dirty?
                                                                  (nil? @*value)))
                                                     true)
                                   ;; Hides the calendar icon, the clear button takes its place
                                   :data-clearable (when clear-button? true)
                                   :class          class-input
                                   :on-blur        (fn [e]
                                                     (when-not (allowed-date? @*value)
                                                       (reset! *value nil)
                                                       (set! (.-value (.-target e)) "")))
                                   :id             (when id (str id "-input"))
                                   :name           "date-picker-input"
                                   :placeholder    placeholder}
               :formatDate        (fn [js-date _ _]
                                    (format-date js-date))
               :day-picker-props  (cond-> {:month          @*month
                                           :modifiers      modifiers
                                           :captionElement caption-element
                                           :navbarElement  date-picker-navbar-element
                                           :weekdaysShort  weekdays-short
                                           :weekdaysLong   weekdays}
                                    (and (some? tr)
                                         (allowed-date? (js/Date.))) (assoc :today-button (tr [:date/today]))
                                    (some? render-day) (assoc :render-day (prepare-renderer render-day)))}]
             (when clear-button?
               (cc/button {:kind             :ghost
                           :size             :small
                           :icon             i/remove
                           :icon-position    :right
                           :icon-description (tr [:clear])
                           :class-name       "only-icon-button absolute right-1 bottom-1"
                           :on-click         #(on-day-change nil nil nil)}
                          ""))
             (when (and invalid? (not (str/blank? invalid-text)))
               [:div.bx--form-requirement invalid-text])]]))})))


(defn datetime-picker [{:keys [on-change tz datetime date-label time-label] :as opts}]
  (let [tz                    (or tz date-util/browser-timezone)
        tz-converted-datetime (date-util/to-tz datetime tz)
        tz-converted-time     (if tz-converted-datetime
                                [(.getHours tz-converted-datetime) (.getMinutes tz-converted-datetime)]
                                [nil nil])
        tz-info               (if (not= tz date-util/browser-timezone)
                                (str " (" (date-util/time-zone-name tz {:locale "en-CA" :format "shortGeneric"}) ")")
                                "")]
    [:div.flex.flex-col.sm:flex-row.mb-4
     [date-picker (merge {:label-text date-label
                          :class-name "md:mr-2"
                          :callback   #(when (some? %)
                                         (let [d        (or tz-converted-datetime (js/Date.))
                                               new-date (doto %
                                                          (.setHours (.getHours d))
                                                          (.setMinutes (.getMinutes d)))]
                                           (on-change (date-util/in-tz new-date tz))))
                          :value      tz-converted-datetime}
                         (select-keys opts [:max-date :min-date :disabled?]))]
     [time-input (merge {:label-text (str time-label tz-info)
                         :class      "mt-4 md:mt-0"
                         :on-change  (fn [[hour minute]]
                                       (let [new-date (if tz-converted-datetime
                                                        (js/Date. (.getFullYear tz-converted-datetime)
                                                                  (.getMonth tz-converted-datetime)
                                                                  (.getDate tz-converted-datetime)
                                                                  hour
                                                                  minute
                                                                  0
                                                                  0)
                                                        (js/Date.))]
                                         (on-change (date-util/in-tz new-date tz))))
                         :value      tz-converted-time}
                        (select-keys opts [:disabled?]))]]))


(defn tag
  "Renders a simple tag as a span element.
   options:
    `type`: `:round` draws rounded borders
    `color`: `:yellow`, `:blue`, `:night-sky`, `:light-gray`, `:white`, `:transparent`
    `class-name`: string containing additional css classes"
  [{:keys [type color class-name data-cy]} text]
  [:span.cloudpermit--tag.cloudpermit--tag--square.m-1
   (p/assoc-if
     {:class [(case color
                :yellow "bg-ui-yellow text-ui-black"
                :blue "cloudpermit--tag--blue"
                :night-sky "bg-ui-night-sky-100 text-ui-white"
                :light-gray "bg-ui-light-gray text-ui-black"
                :white "bg-ui-white text-ui-night-sky-100"
                :transparent "bg-ui-transparent text-ui-night-sky-100"
                "")
              (when (= type :round)
                "cloudpermit--tag--round")
              (when class-name
                class-name)]}
     :data-cy data-cy)
   text])


(defn text-to-paragraphs [text class]
  (futil/for-indexed [[i paragraph] (str/split-lines text)]
    ^{:key i} [:p.p-0 {:class class} paragraph]))



(defn phrase-has-category? [phrase category]
  (= (-> phrase :phrase/category :db/id)
     (:db/id category)))

(defn phrase-selector [{:keys [default-category tr phrases editing? light? id on-change]}]
  ;; This can't be instantiated with 'default-category' because the
  ;; component may be initially mounted with nil values
  (r/with-let [*selected-category (r/atom nil)
               phrase-applicable? #(if-some [category @*selected-category]
                                     (or (nil? category)
                                         (phrase-has-category? % category))
                                     (if default-category
                                       (phrase-has-category? % default-category)
                                       true))]
    (when (seq phrases)
      [:div.flex.flex-col.sm:flex-row.text-area__phrase-selector
       [:div.flex-1.sm:max-w-2-3.max-w-full
        [ui-select/select
         {:id           (str id "-categories")
          :inline       true
          :val->str     :phrase.category/name
          :allow-empty? true
          :class        "full-width-select"
          :empty-text   (tr [:phrase.category/all])
          :value        @*selected-category
          :on-change    #(reset! *selected-category %)
          :required     true
          :disabled     (not editing?)
          :light        (boolean light?)
          :values       (->> phrases
                             (map :phrase/category)
                             distinct
                             (sort-by :phrase.category/name))}]]
       [:div.flex-1
        (when light? {:class "bg-ui-white"})
        ;; carbon components really need functions, keywords don't cut it
        #_{:clj-kondo/ignore [:redundant-fn-wrapper]}
        (cc/combo-box {:id             (str id "-phrases")
                       :class-name     "inline-combo"
                       :on-change      #(-> ^js % .-selectedItem :phrase/text on-change)
                       :placeholder    (tr [:phrase.category/select])
                       :disabled       (not editing?)
                       :light          (boolean light?)
                       :item-to-string #(:phrase/name %)
                       :items          (->> phrases
                                            (filter phrase-applicable?)
                                            (sort-by :phrase/name))})]])))



(defn radio-button-custom [name props]
  (let [id            (:id props)
        value         (:value props)
        content       (:content props)
        checked?      (= (:curr-selected props) value)
        disabled?     (:disabled props)
        content-class (:content-class props)
        radio-hidden? (:radio-hidden? props)]
    [:div {:class (str "bx--radio-button-wrapper  " (:class props))
           :key   (or id (str name "." value))}
     [:input {:type      "radio"
              :class     "bx--radio-button"
              :id        (or id (str name "." value))
              :name      name
              :value     value
              :checked   checked?
              :disabled  disabled?
              :on-change (:on-change props)}]
     [:label {:class "block bx--radio-button__label"
              :for   (or id (str name "." value))}
      [:span
       {:class (if radio-hidden? "hidden" "bx--radio-button__appearance inline-block align-top")}]
      [:span {:class ["pt-3 pb-2 p-6 flex flex-wrap rounded max-w-sm border-solid border-2"
                      (if checked? "bg-ui-blue-20 border-ui-blue-60" "bg-ui-light-gray border-ui-light-gray")
                      (when-not disabled? "hover:bg-ui-blue-20 hover:border-ui-blue-60")
                      (when content-class content-class)]}
       [:div.w-full (when content
                      content)]
       (when (:caption props)
         [:div {:class ["w-full text-center"
                        (when checked? "font-bold")]}
          (:caption props)])]]]))

(defn select-pills
  "A group of mutually exclusive toggleable rounded buttons.

  Required
  :on-change      (fn, accepts the value of the selected option)

  Optional
  :value          (current selected option's value, nil for none)
  :label-text     (string, label for whole group)
  :disabled?      (boolean, true if disabled)
  :class          (string, classes given to the container)
  :option-class   (string, classes given to the options)"
  [{:keys [value label-text on-change disabled? class option-class]} & options]
  (let [new-tags-enabled? (ff/enabled? :feature-flag.id/new-tags)]
    [:div {:class class}
     (when (some? label-text)
       [:div [:label.bx--label label-text]])
     (if (not new-tags-enabled?)
       (for-frag [{:keys [text] opt-value :value} options]

         [:button.rounded-full.border-1.py-2.px-5.mt-2.mr-2.focus:outline-none.border-solid.font-bold
          {:on-click #(when (not disabled?)
                        (on-change opt-value))
           :class    [option-class
                      (if (= opt-value value)
                        (if disabled?
                          "bg-ui-gray text-white border-ui-gray"
                          "bg-ui-violet text-white border-ui-violet")
                        (if disabled?
                          "bg-transparent text-ui-gray border-ui-gray"
                          "bg-transparent text-ui-violet border-ui-violet focus:bg-ui-violet-20 focus:text-ui-violet hover:bg-ui-night-sky-100 hover:text-ui-white hover:border-ui-night-sky-100"))]}
          text])
       [:div.flex.flex-wrap.flex-col.md:flex-row.gap-2
        (for-frag [{:keys [text] opt-value :value} options]
          [ct/tag {:label-text text
                   :disabled?  (boolean disabled?)
                   :on-click   #(when (not disabled?)
                                  (on-change opt-value))
                   :selected?  (= opt-value value)}])])]))

(def pill-sizes #{:normal :small})
(def pill-colors #{:dark-blue :gray :green :yellow :red :white})

(s/defschema PillOptions
  (st/merge {:text (s/maybe (s/cond-pre s/Str s/Int))}
            (st/optional-keys
              {:color (s/maybe (apply s/enum pill-colors))
               :size  (s/maybe (apply s/enum pill-sizes))})))

(def pill-defaults
  {:size  :normal
   :color :dark-blue})

(s/defn pill [opts :- PillOptions]
  (let [{:keys [text size color]} (merge pill-defaults
                                         (p/remove-vals nil? opts))]
    (when text
      [:div.font-bold.text-center.px-3.py-1.flex.items-center.justify-center
       {:class (concat
                 (case size
                   :normal ["rounded-md" "mr-4" "text-xl" "w-14"]
                   :small ["rounded" "mr-3" "text-sm" "w-12"])
                 (case color
                   :green ["bg-status-success"]
                   :gray ["bg-status-neutral-01"]
                   :yellow ["bg-status-warning"]
                   :red ["bg-status-error"]
                   :white ["bg-ui-white"]
                   ["bg-ui-night-sky-100"])
                 (case color
                   (:yellow :white) ["text-ui-night-sky-100"]
                   ["text-ui-white"]))}
       text])))


(def small-pill-kinds #{:primary :secondary :disabled :warning :attention})
(def small-pill-sizes #{:small :medium})

(s/defschema SmallPillOptions
  (st/optional-keys
    {:id         s/Str
     :kind       (apply s/enum small-pill-kinds)
     :data-cy    s/Str
     :on-click   (s/pred fn?)
     :uppercase? s/Bool
     :size       (apply s/enum small-pill-sizes)}))

(s/defn small-pill
  [{:keys [id data-cy kind on-click uppercase? size]
    :or   {kind       :primary
           uppercase? true
           size       :small}} :- SmallPillOptions
   content]
  [:div.text-center.rounded-full.w-fit
   (p/assoc-if
     {:class (-> (case kind
                   :primary ["bg-ui-night-sky" "text-ui-white"]
                   :secondary ["bg-ui-light-gray" "text-ui-night-sky-80"]
                   :disabled ["bg-ui-dark-gray" "text-ui-night-sky"]
                   :warning ["bg-ui-yellow" "text-ui-night-sky"]
                   :attention ["bg-status-attention" "text-ui-night-sky"])
                 (conj (if on-click "cursor-pointer" "cursor-default"))
                 (conj (when uppercase? "uppercase"))
                 (concat (case size
                           :small ["px-3" "py-1"]
                           :medium ["px-5" "py-3"])))}
     :id id
     :data-cy data-cy
     :on-click on-click)
   content])

(defn bubble-content
  "Turn a piece of text into somewhat formatted hickup, with newlines"
  ([text] (bubble-content {} text))
  ([{:keys [class]} text]
   (->> text
        str/split-lines
        (map (partial vector :p.pb-0.pt-2.first:p-0))
        (into [:div {:class class}]))))

(defn- color->class [color]
  (case color
    :blue "bg-ui-blue-10"
    :white "bg-ui-white"
    "bg-ui-light-gray"))

(defn help
  "A help text bubble"
  [content & {:keys [class color]}]
  (when content
    [:div.flex.items-center.rounded.text-ui-night-sky-100.w-fit.gap-4
     {:class [(or class "my-4 p-4")
              (color->class color)]}
     (cc/icon {:icon        i/circle-info
               :fill        "#616161"
               :class-name  "flex-none"
               :aria-hidden true
               :description ""})
     (if (or (string? content) (nil? content))
       [bubble-content content]
       ;; otherwise, it's a component, don't touch it
       content)]))

(defn help-2
  "A full-width help bubble without icon"
  ([text] (help-2 {} text))
  ([{:keys [class color]} text]
   (when text
     [:div.flex.items-center.rounded.text-ui-night-sky-100.gap-4.p-4
      {:class [class
               (color->class color)]}
      [bubble-content text]])))

(defn warning
  ([text] (warning {} text))
  ([{:keys [class content-class]} text]
   [:div.my-4.bg-ui-red-20.p-4.rounded.flex.items-center.gap-4
    {:class class}
    (cc/icon {:icon        i/warning
              :description ""
              :aria-hidden true})
    [bubble-content {:class content-class} text]]))


(defn instructions
  "An instruction area with a light blue background.
   Expects argument map with the following keys:
   :tr         tr-function from sub
   :heading    dictionary-key for main heading (default \"Instructions\")
   :paragraphs [[:subheading-dictionary-key :paragraph-dictionary-key]
                 :just-paragraph-dictionary-key
                 ...]"
  [{:keys [id tr heading paragraphs rt-component]}]
  (r/with-let [local-storage-key (when id
                                   (str id "-visible"))
               previous-state?   (some-> local-storage-key
                                         futil/local-storage-get
                                         (= "true"))
               *visible?         (r/atom (if (some? previous-state?)
                                           previous-state?
                                           true))
               set-state         (fn []
                                   (let [new-state (swap! *visible? not)]
                                     (when local-storage-key
                                       (futil/local-storage-set! local-storage-key (str new-state)))))]
    [:section.bg-ui-blue-10.p-4.shadow-lg (p/assoc-if {} :id id)
     [:div.flex.items-center.justify-between
      [:h2.mb-0
       [:button.bg-transparent.border-0.p-0.text-lg.bold.flex.items-center
        {:on-click set-state}
        [:> Information16]
        [:span.ml-2
         (tr [(or heading
                  :instructions/instructions)])]]]

      [c/chevron {:tr        tr
                  :class     "cursor-pointer"
                  :side      :right
                  :icon      i/chevron-right
                  :expanded? @*visible?
                  :on-click  set-state}]]
     [t/transition
      {:transition "dispatch-slide-down"
       :timeout    200
       :in         @*visible?}
      [:div.mt-3
       (when rt-component
         [:div.p-5.bg-white.rounded-md.mb-5
          rt-component])
       (for-frag [paragraph paragraphs
                  :let [[subheading & contents] (if (vector? paragraph)
                                                  paragraph
                                                  [nil paragraph])]]
         [:div
          (when subheading
            [:h3.text-sm.bold.mb-3 (tr [subheading])])
          [:p.pb-3 (str/join " " (map #(tr [%]) contents))]])]]]))


(defn- icon->permit-type-icon [icon]
  (r/reactify-component
    (fn [props]
      (cc/icon {:icon        icon
                :class-name  (-> props :className (str " fill-current") str/trim)
                :description (:description props "")
                :aria-hidden true
                :width       (:width props "24")
                :height      (:height props "24")}))))


(def work-order-icon (icon->permit-type-icon i/work-order-24))


(defn permit-type-icon [permit-type]
  (case permit-type
    :permit-type/B Building24
    :permit-type/P MapBoundary24
    :permit-type/CE Security24
    :permit-type/PW TrafficCone24
    :permit-type/PWO work-order-icon
    :permit-type/BS Stamp24
    :permit-type/LU MapBoundaryVegetation24
    :permit-type/L License24
    :permit-type/CR Calibrate24
    nil))


(defn permit-type-color [permit-type]
  (case permit-type
    :permit-type/B "ui-blue-110"
    :permit-type/P "ui-blue-150"
    :permit-type/CE "ui-red-140"
    "ui-blue-110"))


(defn subdivision-flag-url
  "Returns the flag URL for a subdivision keyword like :US-TX or :CA-ON,
  or nil when there is no flag for it."
  [subdivision]
  (when subdivision
    (let [[country state-code] (-> subdivision name (str/split #"-"))]
      (when state-code
        (case country
          "US" (str "/asset/img/flags/us-states/" (str/lower-case state-code) ".svg")
          "CA" (str "/asset/img/flags/ca-provinces/" (str/lower-case state-code) ".svg")
          nil)))))


(defn time-zone-info
  "Output a div element containing a text explaining what time zone is currently displayed.
   Options are:
   :group-tz             (string, tzdb time zone name, e.g. America/Toronto)
   :tr-key               (keyword, dictionary key for a text that should include one arg (%1))
   :tz-format            (string, short|long|shortGeneric|longGeneric)
   :hide-when-identical? (bool, do not display if same zone as the browser default)"
  [{:keys [group-tz tz-format tr-key hide-when-identical? class]}]
  (when-let [tz-info (date-util/time-zone-info group-tz tz-format hide-when-identical?)]
    (let [tr (<sub [:tr])]
      [:div {:class class}
       (tr [tr-key]
           [tz-info])]))

  (when (and group-tz tz-format tr-key)
    (let [locale          (<sub [::i18n/full-locale])
          tr              (<sub [:tr])
          opts            {:format (or tz-format "short")
                           :locale locale}
          browser-tz-name (date-util/time-zone-name nil opts)
          group-tz-name   (date-util/time-zone-name group-tz opts)]
      (when (or (not hide-when-identical?)
                (not= browser-tz-name group-tz-name))
        [:div {:class class}
         (tr [tr-key]
             [group-tz-name])]))))

(defn summarize-description
  "Summarizes a description string. Optionally takes a max length argument.
  If max length is not given, returns the description until the first line break.
  Adds an ellipsis at the end if the description is longer than the max length or
  if the description has multiple lines."
  ([description]
   (summarize-description description nil))
  ([description max-length]
   (let [lines           (str/split-lines description)
         first-line      (first lines)
         multiple-lines? (du/at-least-two? lines)
         ellipsis        "..."]
     (cond
       (and multiple-lines? (nil? max-length))
       (str first-line ellipsis)

       (and max-length (> (count first-line) max-length))
       (str (subs first-line 0 max-length) ellipsis)

       :else
       first-line))))

(def show-more-button
  :button.p-0.border-0.bg-transparent.text-blue-500.font-bold.text-sm)

(defn show-more-text [tr max-length text]
  (r/with-let [*expanded? (r/atom false)]
    (if (> (count text) max-length)
      (let [desc-preview (summarize-description text max-length)
            button-title (if @*expanded? (tr [:show-less]) (tr [:show-more]))
            content      (if @*expanded? text desc-preview)]
        [:div
         (text-to-paragraphs content "mb-2 pr-2 leading-snug")
         [show-more-button {:on-click #(swap! *expanded? not)} button-title]])
      [:p.p-0.pr-2.leading-snug text])))


(defn tag-button [{:keys [href selected? status on-click icon icon-description light?]} label]
  [(if href :a :button)
   (p/assoc-if
     {:class ["flex flex-row items-center rounded appearance-none border-0 border-l-4 border-solid py-2 px-4
                 font-bold text-left no-underline"
              (if selected?
                "bg-ui-night-sky-100 text-ui-white"
                (str (if light? "bg-ui-white" "bg-ui-night-sky-10")
                     " text-ui-night-sky-100 focus:bg-ui-violet-20 focus:text-ui-night-sky-100
                     hover:bg-ui-violet-100 hover:text-ui-white"))
              (case status
                :success "border-ui-green"
                :warning "border-ui-yellow"
                :attention "border-status-attention"
                :error "border-ui-red-100"
                "border-ui-mid-gray")]}
     :href href
     :on-click on-click)
   (when icon
     (cc/icon {:icon        icon
               :description (or icon-description "")
               :aria-hidden (nil? icon-description)
               :class-name  "mr-2 -ml-1"
               :width       "12"
               :fill        (case status
                              :success "var(--ui-green)"
                              :warning "var(--ui-yellow)"
                              :attention "var(--status-attention)"
                              :error "var(--ui-red-100)"
                              "var(--ui-blue-30)")}))
   label])


(defn heading-with-status [{:keys [status element]} & children]
  (into
    [(or element :h1)
     {:class "font-bold text-ui-night-sky-100 uppercase text-lg relative pl-4"}
     [:div
      {:class ["inline-block rounded mr-2 absolute left-0 -top-0.5 -bottom-0.5 w-1"
               (case status
                 :success "bg-ui-green"
                 :warning "bg-ui-yellow"
                 :attention "bg-status-attention"
                 :error "bg-ui-red-100"
                 "bg-ui-mid-gray")]
       :role  "presentation"}]]
    children))

(defn color-input [{:keys [id label-text value on-change]}]
  [:div.bx--form-item
   [:label.bx--label {:for id} label-text]
   [:div.flex.items-center.gap-2
    [:input (util/mmap {:type :color}
                       id value on-change)]
    (when value
      [:div.font-bold value])]])
